import {
  Button,
  ButtonTypes,
  IError,
  IProfile,
  LoadingOverlay,
  useToast,
} from '@comptia-sso/core';
import { ReactElement, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from 'react-query';

// Components.
import { Card, ConfirmationModal } from 'components';

// Hooks.
import { useDeleteProfile } from 'hooks';

// Styles.
import styles from './DeleteProfile.module.scss';

interface IDeleteProfileProps {
  profile: IProfile;
}

export const DeleteProfile = (props: IDeleteProfileProps): ReactElement => {
  const { profile } = props;
  const [popToast] = useToast();
  const [isConfirming, setIsConfirming] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteProfile] = useDeleteProfile(profile?.id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const handleDeleteClick = () => {
    setIsConfirming(true);
  };

  const handleConfirm = async () => {
    setIsConfirming(false);
    setIsDeleting(true);

    try {
      await deleteProfile();
      queryClient.removeQueries(['profiles']);
      popToast('Profile has been permanently deleted.');
      navigate('/');
    } catch (error) {
      setIsDeleting(false);
      popToast(
        (error as IError)?.message ||
          'An unexpected error occurred. Please try again later.',
      );
    }
  };

  const handleCancel = () => {
    setIsConfirming(false);
  };

  if (!profile || profile?.archived) return <></>;

  return (
    <section title="Delete Account">
      <header>
        <h2>Delete Account</h2>
      </header>
      <Card className={styles.content}>
        <p className={styles.warning}>
          This action will permanently delete the user&apos;s account and cannot
          be undone. This includes their SSO login and CompTIA Central record.
          If this is a GDPR request, please contact any external systems/vendors
          to delete their data as well.
        </p>
        <Button
          className={styles.button}
          type={ButtonTypes.Button}
          onClick={handleDeleteClick}
          disabled={profile?.archived}
        >
          Delete Account
        </Button>
        <ConfirmationModal
          confirmationMessage="Are you sure you want to permanently delete this account? This action cannot be undone."
          open={isConfirming}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
        <LoadingOverlay isOpen={isDeleting} text="Deleting Profile" />
      </Card>
    </section>
  );
};
